import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import httpClient from './axios'

export interface ApiResponse<T> {
  success: boolean
  message: string | null
  data: T
}

export interface ApiError {
  message: string
  status?: number
  code?: string
  details?: any
}

const handleApiError = (error: AxiosError | any): ApiError => {
  const apiError: ApiError = {
    message: 'Something went wrong. Please try again.',
  }

  if (error?.response) {
    apiError.status = error.response.status
    apiError.code = error.code

    const responseData = error.response.data
    apiError.message = responseData?.message || error.message || 'Server error occurred'
    apiError.details = error.response.data
  } else if (error?.request) {
    apiError.message = 'No response from server. Please check your connection.'
    apiError.code = 'NETWORK_ERROR'
  } else {
    apiError.message = error.message || 'Request setup failed'
    apiError.code = error.code
  }
  return apiError
}

const handleSuccess = <T>(response: AxiosResponse<T>, showMessage: boolean = false): T => {
  const data = response.data as any

  if (data && typeof data === 'object' && 'success' in data) {
    if (!data.success) {
      const error: ApiError = {
        message: data.message || 'Request failed',
        status: response.status,
        details: data,
      }
      throw error
    }
    return data.data as T
  }
  return response.data
}

export const apiClient = {
  get: async <T>(url: string, config?: AxiosRequestConfig, showMessage?: boolean): Promise<T> => {
    try {
      const res = await httpClient.get<T>(url, config)
      return handleSuccess<T>(res, showMessage)
    } catch (error) {
      throw handleApiError(error)
    }
  },

  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig, showMessage?: boolean, ignoreResponseValidation?: boolean): Promise<T> => {
    try {
      const res = await httpClient.post<T>(url, data, {
        ...config,
        headers: {
          ...config?.headers,
          ...(ignoreResponseValidation ? { 'X-Ignore-Response-Validation': 'true' } : {}),
        },
      })
      return handleSuccess<T>(res, showMessage)
    } catch (error) {
      throw handleApiError(error)
    }
  },

  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig, showMessage?: boolean): Promise<T> => {
    try {
      const res = await httpClient.put<T>(url, data, config)
      return handleSuccess<T>(res, showMessage)
    } catch (error) {
      throw handleApiError(error)
    }
  },

  patch: async <T>(url: string, data?: any, config?: AxiosRequestConfig, showMessage?: boolean): Promise<T> => {
    try {
      const res = await httpClient.patch<T>(url, data, config)
      return handleSuccess<T>(res, showMessage)
    } catch (error) {
      throw handleApiError(error)
    }
  },

  delete: async <T>(url: string, config?: AxiosRequestConfig, showMessage?: boolean): Promise<T> => {
    try {
      const res = await httpClient.delete<T>(url, config)
      return handleSuccess<T>(res, showMessage)
    } catch (error) {
      throw handleApiError(error)
    }
  },

  upload: async <T>(
    url: string,
    formData: FormData,
    config?: AxiosRequestConfig,
    onProgress?: (progress: number) => void,
  ): Promise<T> => {
    try {
      const res = await httpClient.post<T>(url, formData, {
        ...config,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...config?.headers,
        },
        onUploadProgress: progressEvent => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        },
      })
      return handleSuccess<T>(res, false)
    } catch (error) {
      throw handleApiError(error)
    }
  },

  download: async (url: string, filename?: string): Promise<void> => {
    try {
      const response = await httpClient.get(url, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data])
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      throw handleApiError(error)
    }
  },
}

export { httpClient as axios }
